/**
 * Kashf.ai - AI-Powered Procurement Discovery Platform
 * Main application entry point
 */

// Silence console logs in production
if (!import.meta.env.DEV && import.meta.env.VITE_DEBUG !== 'true') {
  const noop = () => {};
  console.log = noop;
  console.info = noop;
  console.debug = noop;
  console.warn = noop;
  // Keep console.error for critical issues
}

import {
  renderDashboard,
  renderSettings,
} from './pages/index.js';

import * as uploadPage from './pages/upload/index.js';
import * as contractsPage from './pages/contracts/index.js';
import * as invoicesPage from './pages/invoices/index.js';
import * as vendorsPage from './pages/vendors/index.js';
import * as renewalsPage from './pages/renewals/index.js';
import * as discrepanciesPage from './pages/discrepancies/index.js';

import {
  t,
  setLanguage,
  getCurrentLanguage,
  applyTranslations,
  setCurrency
} from './i18n/translations.js';

import { init as initGlobalSearch } from './components/search/GlobalSearchDropdown.js';
import { init as initNotifications, injectStyles as injectNotificationStyles } from './components/notifications/NotificationDropdown.js';
import { init as initCopilot } from './components/copilot/CopilotPanel.js';
import { apiClient, getAuthToken } from './services/api.js';
import { auth } from './services/auth.js';

/**
 * Fetch and display organization branding (logo and name)
 * Uses /v1/organizations/me endpoint to get current user's organization
 */
async function loadOrganizationBranding() {
  // Check if user is super_admin from token - don't show org branding for them
  const token = getAuthToken();
  if (token) {
    try {
      const parts = token.split('.');
      if (parts.length >= 2) {
        const payload = JSON.parse(atob(parts[1]));
        const role = payload.extension_Role || payload.role || 'user';
        if (role === 'super_admin') {
          const orgBranding = document.getElementById('orgBranding');
          if (orgBranding) orgBranding.style.display = 'none';
          return;
        }
      }
    } catch (e) {
      // Continue with loading
    }
  }

  try {
    const response = await apiClient.get('/v1/organizations/me');
    const org = response?.data?.data || response?.data || response;

    if (!org) return;

    const orgBranding = document.getElementById('orgBranding');
    const orgLogoPlaceholder = document.getElementById('orgLogoPlaceholder');
    const orgLogoImg = document.getElementById('orgLogoImg');
    const orgName = document.getElementById('orgName');

    if (!orgBranding) return;

    // Show the branding section
    orgBranding.style.display = 'flex';

    // Set organization name
    if (orgName) {
      orgName.textContent = org.name || 'Organization';
      orgName.title = org.name || ''; // Tooltip for long names
    }

    // Set organization logo
    const logoUrl = org.logoUrl || org.logo_url;
    if (logoUrl && orgLogoImg && orgLogoPlaceholder) {
      orgLogoImg.src = logoUrl;
      orgLogoImg.style.display = 'block';
      orgLogoPlaceholder.style.display = 'none';

      // Handle image load error
      orgLogoImg.onerror = () => {
        orgLogoImg.style.display = 'none';
        orgLogoPlaceholder.style.display = 'flex';
      };
    } else if (orgLogoPlaceholder) {
      // Generate initials for placeholder
      const name = org.name || 'ORG';
      const words = name.split(' ').filter(w => w.length > 0);
      const initials = words.length >= 2
        ? (words[0][0] + words[1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
      orgLogoPlaceholder.textContent = initials;
    }

    console.log('[Main] Organization branding loaded:', org.name);
  } catch (error) {
    console.warn('[Main] Could not load organization branding:', error);
    // Hide branding on error
    const orgBranding = document.getElementById('orgBranding');
    if (orgBranding) orgBranding.style.display = 'none';
  }
}

/**
 * Update user profile in sidebar from user data
 * @param {Object} userData - User data from API
 */
function updateUserProfile(userData) {
  const userNameEl = document.getElementById('userName');
  const userRoleEl = document.getElementById('userRole');
  const userAvatarEl = document.getElementById('userAvatar');

  if (!userNameEl || !userRoleEl || !userAvatarEl) return;

  // Get name from various possible fields
  const name = userData?.name || userData?.displayName || userData?.display_name ||
               `${userData?.givenName || userData?.given_name || ''} ${userData?.familyName || userData?.family_name || ''}`.trim() ||
               userData?.email?.split('@')[0] || 'User';

  // Get role from user data
  const role = userData?.role || userData?.extension_Role || 'User';

  // Format role for display
  const roleDisplay = role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ');

  // Generate initials for avatar
  const nameParts = name.split(' ');
  const initials = nameParts.length >= 2
    ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase()
    : name.substring(0, 2).toUpperCase();

  userNameEl.textContent = name;
  userRoleEl.textContent = roleDisplay;
  userAvatarEl.textContent = initials;

  // Get orgId from userData to verify super_admin status
  const orgId = userData?.organizationId || userData?.extension_OrganizationId;
  const isSuperAdmin = role === 'super_admin' && orgId && orgId !== 'pending';

  // Admin nav section - inject HTML only for verified super_admin
  const adminNavSection = document.getElementById('adminNavSection');
  if (adminNavSection) {
    if (isSuperAdmin) {
      injectAdminNavForSuperAdmin();
      adminNavSection.style.display = 'block';
    } else {
      adminNavSection.innerHTML = '';
      adminNavSection.style.display = 'none';
    }
  }

  // Org-level nav items - hide for super_admin, show for others
  const orgNavPages = ['dashboard', 'contracts', 'invoices', 'vendors', 'renewals', 'analysis', 'optimization', 'audit'];
  orgNavPages.forEach(page => {
    const navItem = document.querySelector(`.nav-item[data-page="${page}"]`);
    if (navItem) {
      navItem.style.display = isSuperAdmin ? 'none' : 'flex';
    }
  });

  // Header elements - hide org-specific elements for super_admin
  const searchBox = document.querySelector('.search-box');
  const uploadBtn = document.getElementById('uploadBtn');
  const notificationBtn = document.getElementById('notificationBtn');

  if (searchBox) searchBox.style.display = isSuperAdmin ? 'none' : 'flex';
  if (uploadBtn) uploadBtn.style.display = isSuperAdmin ? 'none' : 'inline-flex';
  if (notificationBtn) notificationBtn.style.display = isSuperAdmin ? 'none' : 'inline-flex';
}

/**
 * Inject admin navigation HTML for super_admin users only
 * This is a security measure - admin nav is NOT in static HTML
 */
function injectAdminNavForSuperAdmin() {
  const adminNavSection = document.getElementById('adminNavSection');
  if (!adminNavSection) return;

  // Only inject if not already done
  if (adminNavSection.dataset.injected === 'true') return;

  adminNavSection.className = 'nav-section nav-section--admin';
  adminNavSection.innerHTML = `
    <div class="nav-section-title" data-i18n="nav.platformAdmin">Platform Admin</div>
    <a href="#admin" class="nav-item nav-item--admin" data-page="admin">
      <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="3" width="7" height="7" rx="1"/>
        <rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="3" y="14" width="7" height="7" rx="1"/>
        <rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
      <span data-i18n="nav.adminDashboard">Dashboard</span>
    </a>
    <a href="#admin/organizations" class="nav-item nav-item--admin" data-page="admin/organizations">
      <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M3 21h18"/>
        <path d="M5 21V7l8-4v18"/>
        <path d="M19 21V11l-6-4"/>
        <path d="M9 9v.01"/><path d="M9 12v.01"/><path d="M9 15v.01"/><path d="M9 18v.01"/>
      </svg>
      <span data-i18n="nav.organizations">Organizations</span>
    </a>
    <a href="#admin/users" class="nav-item nav-item--admin" data-page="admin/users">
      <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
      <span data-i18n="nav.allUsers">All Users</span>
    </a>
    <a href="#admin/analytics" class="nav-item nav-item--admin" data-page="admin/analytics">
      <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="18" y1="20" x2="18" y2="10"/>
        <line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
      <span data-i18n="nav.platformAnalytics">Analytics</span>
    </a>
    <a href="#admin/plans" class="nav-item nav-item--admin" data-page="admin/plans">
      <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="7.5 4.21 12 6.81 16.5 4.21"/>
        <polyline points="7.5 19.79 7.5 14.6 3 12"/>
        <polyline points="21 12 16.5 14.6 16.5 19.79"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
        <line x1="12" y1="22.08" x2="12" y2="12"/>
      </svg>
      <span data-i18n="nav.plansProviders">Plans & AI</span>
    </a>
    <a href="#admin/billing" class="nav-item nav-item--admin" data-page="admin/billing">
      <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
      <span data-i18n="nav.billingUsage">Billing & Usage</span>
    </a>
  `;
  adminNavSection.dataset.injected = 'true';

  // Re-apply translations to new elements
  applyTranslations();

  console.log('[Main] Admin nav injected for super_admin');
}

/**
 * Update navigation visibility based on user role from token
 */
function updateNavVisibilityFromToken() {
  const token = getAuthToken();
  if (!token) return;

  try {
    const parts = token.split('.');
    if (parts.length < 2) return;

    const payload = JSON.parse(atob(parts[1]));
    const role = payload.extension_Role || payload.role || 'user';
    const orgId = payload.extension_OrganizationId;

    // Verify user is actually super_admin with valid org (not pending)
    const isSuperAdmin = role === 'super_admin' && orgId && orgId !== 'pending';

    // Admin nav section - inject HTML only for verified super_admin
    const adminNavSection = document.getElementById('adminNavSection');
    if (adminNavSection) {
      if (isSuperAdmin) {
        injectAdminNavForSuperAdmin();
        adminNavSection.style.display = 'block';
      } else {
        // Clear any injected content and hide
        adminNavSection.innerHTML = '';
        adminNavSection.style.display = 'none';
      }
    }

    // Org-level nav items - hide for super_admin, show for others
    const orgNavPages = ['dashboard', 'contracts', 'invoices', 'vendors', 'renewals', 'analysis', 'optimization', 'audit'];
    orgNavPages.forEach(page => {
      const navItem = document.querySelector(`.nav-item[data-page="${page}"]`);
      if (navItem) {
        navItem.style.display = isSuperAdmin ? 'none' : 'flex';
      }
    });

    // Settings is visible for all roles
    const settingsNav = document.querySelector('.nav-item[data-page="settings"]');
    if (settingsNav) {
      settingsNav.style.display = 'flex';
    }

    // Header elements - hide org-specific elements for super_admin
    const searchBox = document.querySelector('.search-box');
    const uploadBtn = document.getElementById('uploadBtn');
    const notificationBtn = document.getElementById('notificationBtn');

    if (searchBox) searchBox.style.display = isSuperAdmin ? 'none' : 'flex';
    if (uploadBtn) uploadBtn.style.display = isSuperAdmin ? 'none' : 'inline-flex';
    if (notificationBtn) notificationBtn.style.display = isSuperAdmin ? 'none' : 'inline-flex';

    console.log(`[Main] Nav visibility updated for role: ${role}, isSuperAdmin: ${isSuperAdmin}`);
  } catch (e) {
    console.warn('Could not decode auth token for nav visibility:', e);
  }
}

// Backwards compatibility alias
function updateAdminNavFromToken() {
  updateNavVisibilityFromToken();
}

/**
 * Update user profile from auth token when API fails
 */
function updateUserProfileFromToken() {
  const token = getAuthToken();
  if (!token) return;

  try {
    // Decode JWT payload (base64)
    const parts = token.split('.');
    if (parts.length < 2) return;

    const payload = JSON.parse(atob(parts[1]));
    const role = payload.extension_Role || payload.role || 'user';
    updateUserProfile({
      name: payload.name,
      email: payload.email,
      role: role
    });

    // Show admin nav for super_admin from token
    const adminNavItem = document.getElementById('adminNavItem');
    if (adminNavItem && role === 'super_admin') {
      adminNavItem.style.display = 'flex';
    }
  } catch (e) {
    console.warn('Could not decode auth token for user profile:', e);
  }
}

// Export t function globally for use in pages
window.t = t;

// Page titles mapping (now using translation keys)
const pageTitleKeys = {
  dashboard: 'nav.dashboard',
  contracts: 'nav.contracts',
  invoices: 'nav.invoices',
  renewals: 'nav.renewals',
  analysis: 'nav.analysis',
  integrations: 'nav.integrations',
  optimization: 'nav.optimization',
  audit: 'nav.audit',
  settings: 'nav.settings',
  upload: 'upload.title'
};

// Page renderers mapping (most pages use special handling with dynamic imports)
const pageRenderers = {
  dashboard: renderDashboard,
  settings: renderSettings,
};

/**
 * Parse hash to extract page and query parameters
 */
function parseHash(hash) {
  // Remove leading # and /
  let cleanHash = hash.startsWith('#') ? hash.slice(1) : hash;
  if (cleanHash.startsWith('/')) cleanHash = cleanHash.slice(1);

  // Split by ? to get page and query string
  const [page, queryString] = cleanHash.split('?');
  const query = {};

  if (queryString) {
    const params = new URLSearchParams(queryString);
    for (const [key, value] of params) {
      query[key] = value;
    }
  }

  return { page: page || 'dashboard', query };
}

/**
 * Setup sign out button handler
 */
function setupSignOutButton() {
  const signOutBtn = document.getElementById('signOutBtn');
  if (signOutBtn) {
    signOutBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Immediately hide copilot, sidebar, and header before sign out
      const copilotPanel = document.getElementById('copilotPanel');
      const sidebar = document.querySelector('.sidebar');
      const header = document.querySelector('.main-header');
      if (copilotPanel) copilotPanel.style.display = 'none';
      if (sidebar) sidebar.style.display = 'none';
      if (header) header.style.display = 'none';

      try {
        await auth.signOut();
        // signOut will redirect to login page
      } catch (error) {
        console.error('Sign out error:', error);
        // Force redirect to login even on error
        window.location.hash = '#/login';
      }
    });
  }
}

/**
 * Check if current page is an auth page
 */
function isAuthPage(page) {
  return page === 'login' || page === 'register' || page === 'verify-email' || page === 'forgot-password' || page === 'auth/callback' || page.startsWith('accept-invitation/');
}

/**
 * Handle path-based OAuth callback (e.g., /auth/callback?access_token=...)
 * This runs before hash-based routing since OAuth redirects use path, not hash
 */
function handlePathBasedOAuthCallback() {
  const path = window.location.pathname;
  if (!path.includes('/auth/callback')) {
    return false;
  }

  // Extract tokens from URL query parameters
  const params = new URLSearchParams(window.location.search);
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  const expiresIn = params.get('expires_in');

  if (!accessToken) {
    console.error('[Main] No access token in OAuth callback URL');
    window.location.replace('/#/login');
    return true;
  }

  // Parse user from token
  let user = null;
  try {
    const parts = accessToken.split('.');
    if (parts.length >= 2) {
      const payload = JSON.parse(atob(parts[1]));
      user = {
        id: payload.sub || payload.oid,
        email: payload.email || payload.emails?.[0] || payload.preferred_username,
        name: payload.name,
        role: payload.extension_Role || payload.role,
        organizationId: payload.extension_OrganizationId,
      };
    }
  } catch (e) {
    console.warn('[Main] Could not parse token:', e);
  }

  // Store in localStorage
  const expiresAt = new Date(Date.now() + (parseInt(expiresIn) || 3600) * 1000);
  const authData = {
    accessToken,
    refreshToken,
    expiresAt: expiresAt.toISOString(),
    user,
  };
  localStorage.setItem('auth', JSON.stringify(authData));
  console.log('[Main] OAuth tokens saved, redirecting to app');

  // Redirect to hash-based home route
  window.location.replace('/#/');
  return true;
}

/**
 * Initialize the application
 */
async function init() {
  // Initialize telemetry (Sentry + Google Analytics)
  try {
    const { initializeTelemetry } = await import('./services/telemetry.js');
    await initializeTelemetry({
      sentryDsn: import.meta.env.VITE_SENTRY_DSN,
      gaMeasurementId: import.meta.env.VITE_GA_MEASUREMENT_ID,
      environment: import.meta.env.MODE,
      serviceVersion: '0.1.0',
    });
  } catch (e) {
    console.error('[Main] Failed to initialize telemetry:', e);
  }

  // Handle path-based OAuth callback first (before any other logic)
  if (handlePathBasedOAuthCallback()) {
    return;
  }

  // Ensure auth state is loaded from localStorage before any checks
  auth.initialize();

  // Check if we're on an auth page FIRST - hide sidebar/header immediately to prevent flash
  const { page: initialPage, query: initialQuery } = parseHash(window.location.hash);

  if (isAuthPage(initialPage)) {
    // Hide sidebar, header, and copilot immediately for auth pages
    const sidebar = document.querySelector('.sidebar');
    const header = document.querySelector('.main-header');
    const mainContent = document.querySelector('.main-content');
    const copilotPanel = document.getElementById('copilotPanel');

    if (sidebar) sidebar.style.display = 'none';
    if (header) header.style.display = 'none';
    if (mainContent) {
      mainContent.style.marginLeft = '0';
      mainContent.style.width = '100%';
    }
    // Hide copilot panel on auth pages to prevent data exposure
    if (copilotPanel) copilotPanel.style.display = 'none';

    // Only set up minimal required components for auth pages
    setupLanguageSwitcher();
    applyTranslations();

    // Render the auth page
    navigateTo(initialPage, initialQuery);

    // Listen for hash changes (in case they navigate away from auth page)
    window.addEventListener('hashchange', async () => {
      console.log('[Main] Hashchange detected (from auth page):', window.location.hash);
      try {
        const { page, query } = parseHash(window.location.hash);

        // Re-initialize auth state from localStorage to ensure we have the latest state
        // This is necessary because login updates localStorage and we need to sync in-memory state
        auth.initialize();

        console.log('[Main] Parsed page:', page, 'isAuthPage:', isAuthPage(page), 'isAuthenticated:', auth.isAuthenticated());
        // If navigating to a non-auth page, check authentication first
        if (!isAuthPage(page)) {
          if (!auth.isAuthenticated()) {
            console.log('[Main] User not authenticated, redirecting to login');
            // Only show "session expired" if user had a previous session
            const reason = auth.hadPreviousSession() ? '?reason=session_expired' : '';
            window.location.hash = '#/login' + reason;
            return;
          }
          console.log('[Main] Setting up full app...');
          await setupFullApp();
          console.log('[Main] Full app setup complete');

          // Immediately update profile from token (fast)
          updateUserProfileFromToken();

          // Then fetch full user data from API (slower but more complete)
          try {
            const response = await apiClient.get('/v1/users/me');
            const userData = response?.data?.data || response?.data || response;
            if (userData?.preferences?.currency) {
              setCurrency(userData.preferences.currency);
            }
            if (userData?.preferences?.language) {
              setLanguage(userData.preferences.language);
              applyTranslations();
            }
            updateUserProfile(userData);
            loadOrganizationBranding();
            updateNavVisibilityFromToken();
          } catch (error) {
            console.log('Could not fetch user data, token info already applied');
          }
        }
        console.log('[Main] Calling navigateTo:', page);
        navigateTo(page, query);
        console.log('[Main] Navigation complete');
      } catch (error) {
        console.error('[Main] Error during navigation after login:', error);
        // Force page reload as fallback to complete login
        window.location.reload();
      }
    });

    return;
  }

  // For non-auth pages, check if user is authenticated first
  if (!auth.isAuthenticated()) {
    console.log('[Main] User not authenticated, redirecting to login');
    // Only show "session expired" if user had a previous session
    const reason = auth.hadPreviousSession() ? '?reason=session_expired' : '';

    // Hide sidebar, header, and copilot for login page
    const sidebar = document.querySelector('.sidebar');
    const header = document.querySelector('.main-header');
    const mainContent = document.querySelector('.main-content');
    const copilotPanel = document.getElementById('copilotPanel');
    if (sidebar) sidebar.style.display = 'none';
    if (header) header.style.display = 'none';
    if (mainContent) {
      mainContent.style.marginLeft = '0';
      mainContent.style.width = '100%';
    }
    // Hide copilot panel on auth pages to prevent data exposure
    if (copilotPanel) copilotPanel.style.display = 'none';

    // Set up minimal components and navigate to login
    setupLanguageSwitcher();
    applyTranslations();
    window.location.hash = '#/login' + reason;
    navigateTo('login', reason ? { reason: 'session_expired' } : {});
    return;
  }

  // For non-auth pages, set up the full application
  await setupFullApp();

  // Handle initial route - use default view if no hash is set
  let defaultView = 'dashboard';
  try {
    const response = await apiClient.get('/v1/users/me');
    const userData = response?.data?.data || response?.data || response;
    if (userData?.preferences?.default_view || userData?.preferences?.defaultView) {
      defaultView = userData.preferences.default_view || userData.preferences.defaultView;
    }
    // Set user's preferred currency
    if (userData?.preferences?.currency) {
      setCurrency(userData.preferences.currency);
    }
    // Set user's preferred language
    if (userData?.preferences?.language) {
      setLanguage(userData.preferences.language);
      applyTranslations();
    }
    // Update user profile in sidebar
    updateUserProfile(userData);

  } catch (error) {
    console.log('Could not fetch user preferences, using default view');
    // Try to get user from auth token
    updateUserProfileFromToken();
  }

  // Load organization branding (logo and name) for org users
  loadOrganizationBranding();

  // Always check token for role-based nav visibility
  updateNavVisibilityFromToken();

  // Override default view for super_admin
  const token = getAuthToken();
  if (token) {
    try {
      const parts = token.split('.');
      if (parts.length >= 2) {
        const payload = JSON.parse(atob(parts[1]));
        const role = payload.extension_Role || payload.role || 'user';
        if (role === 'super_admin') {
          defaultView = 'admin';
        }
      }
    } catch (e) {
      // Ignore token parsing errors
    }
  }

  const pageToLoad = (window.location.hash === '' || window.location.hash === '#' || window.location.hash === '#/')
    ? defaultView
    : initialPage;
  navigateTo(pageToLoad, initialQuery);

  // Listen for hash changes
  window.addEventListener('hashchange', () => {
    const { page, query } = parseHash(window.location.hash);
    // Check auth for non-auth pages
    if (!isAuthPage(page) && !auth.isAuthenticated()) {
      console.log('[Main] User not authenticated, redirecting to login');
      // Only show "session expired" if user had a previous session
      const reason = auth.hadPreviousSession() ? '?reason=session_expired' : '';
      window.location.hash = '#/login' + reason;
      return;
    }
    navigateTo(page, query);
  });
}

/**
 * Set up the full application (for non-auth pages)
 */
let fullAppInitialized = false;
async function setupFullApp() {
  // FIRST: Clear page content immediately to prevent login page SVGs from flashing
  const pageContent = document.getElementById('pageContent');
  if (pageContent) {
    pageContent.innerHTML = '';
  }

  // Clean up any leftover login page styles (safety net) - always run this
  const loginBgOverride = document.getElementById('login-page-bg-override');
  if (loginBgOverride) {
    loginBgOverride.remove();
  }

  // Also remove inline login styles if they exist
  const loginStyles = document.getElementById('login-page-styles');
  if (loginStyles) {
    loginStyles.remove();
  }

  // Remove the auth-layout-hide style from index.html
  const authLayoutHide = document.getElementById('auth-layout-hide');
  if (authLayoutHide) {
    authLayoutHide.remove();
  }

  // Force reset background colors to ensure login page dark theme is removed
  document.documentElement.style.background = '';
  document.body.style.background = '';
  const appEl = document.querySelector('.app');
  if (appEl) appEl.style.background = '';

  // Remove login page style overrides that may persist
  const bgOverride = document.getElementById('login-page-bg-override');
  if (bgOverride) bgOverride.remove();

  // Restore sidebar and header visibility
  const sidebar = document.querySelector('.sidebar');
  const header = document.querySelector('.main-header');
  const mainContent = document.querySelector('.main-content');

  if (sidebar) {
    sidebar.style.display = '';
  }
  if (header) {
    header.style.display = '';
  }
  if (mainContent) {
    mainContent.style.marginLeft = '';
    mainContent.style.width = '';
  }

  if (fullAppInitialized) return;
  fullAppInitialized = true;

  setupNavigation();
  setupUploadModal();
  setupDetailModals();
  setupLanguageSwitcher();
  setupSignOutButton();

  // Initialize global search
  initGlobalSearch();

  // Initialize notifications
  injectNotificationStyles();
  initNotifications();

  // Initialize copilot AI assistant
  initCopilot();

  // Apply initial language
  applyTranslations();
}

/**
 * Setup language switcher
 */
function setupLanguageSwitcher() {
  const langBtn = document.getElementById('langBtn');
  const dropdown = document.getElementById('langDropdown');
  const langOptions = document.querySelectorAll('.lang-option');
  
  // Set French as default if no language stored
  if (!localStorage.getItem('invoaice-lang')) {
    setLanguage('fr');
  }
  
  // Update current language button
  const currentLang = getCurrentLanguage();
  updateLangButton(currentLang);
  
  // Toggle dropdown on button click
  if (langBtn) {
    langBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dropdown?.classList.toggle('show');
    });
  }
  
  // Language option click
  langOptions.forEach(option => {
    option.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const lang = option.dataset.lang;
      setLanguage(lang);
      updateLangButton(lang);
      dropdown?.classList.remove('show');
      
      // Re-render current page
      const currentPage = window.location.hash.slice(1) || 'dashboard';
      navigateTo(currentPage);
    });
  });
  
  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.language-switcher')) {
      dropdown?.classList.remove('show');
    }
  });
}

/**
 * Update language button display
 */
function updateLangButton(lang) {
  const langBtn = document.getElementById('langBtn');
  if (!langBtn) return;
  
  const flags = {
    en: '🇬🇧',
    fr: '🇫🇷',
    ar: '🇸🇦'
  };
  
  langBtn.textContent = flags[lang] || '🌐';
  
  // Update active state in dropdown
  document.querySelectorAll('.lang-option').forEach(opt => {
    opt.classList.toggle('active', opt.dataset.lang === lang);
  });
}

/**
 * Setup navigation
 */
function setupNavigation() {
  const navItems = document.querySelectorAll('.nav-item[data-page]');
  
  navItems.forEach(item => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      const page = item.dataset.page;
      window.location.hash = page;
    });
  });
}

// Track current auth page module for cleanup
let currentAuthPageModule = null;
let currentAuthPageName = null;

/**
 * Render authentication page (full screen without sidebar)
 */
async function renderAuthPage(page) {
  console.log('[Main] renderAuthPage called for:', page);

  // Clean up previous auth page if exists
  if (currentAuthPageModule && currentAuthPageName !== page) {
    console.log('[Main] Cleaning up previous auth page:', currentAuthPageName);
    const destroyFnName = `destroy${currentAuthPageName.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('')}Page`;
    if (currentAuthPageModule[destroyFnName]) {
      currentAuthPageModule[destroyFnName]();
    } else if (currentAuthPageModule.destroy) {
      currentAuthPageModule.destroy();
    }
    currentAuthPageModule = null;
    currentAuthPageName = null;
  }

  // Also clean up any lingering auth page styles (e.g., login dark background)
  const loginBgOverride = document.getElementById('login-page-bg-override');
  if (loginBgOverride && page !== 'login') {
    loginBgOverride.remove();
  }

  // Hide sidebar and header for full-screen auth experience
  const sidebar = document.querySelector('.sidebar');
  const header = document.querySelector('.main-header');
  const mainContent = document.querySelector('.main-content');
  const copilotPanel = document.getElementById('copilotPanel');

  console.log('[Main] Found elements:', { sidebar: !!sidebar, header: !!header, mainContent: !!mainContent });

  if (sidebar) sidebar.style.display = 'none';
  if (header) header.style.display = 'none';
  if (mainContent) {
    mainContent.style.marginLeft = '0';
    mainContent.style.width = '100%';
  }
  // Hide copilot panel on auth pages to prevent data exposure
  if (copilotPanel) copilotPanel.style.display = 'none';

  const pageContent = document.getElementById('pageContent');
  if (!pageContent) return;

  // Map page names to their container IDs
  const authPageContainers = {
    'login': 'login-page',
    'register': 'register-page',
    'verify-email': 'verify-email-page',
    'forgot-password': 'forgot-password-page',
  };

  const containerId = authPageContainers[page];
  if (!containerId) {
    console.error('[Main] Unknown auth page:', page);
    return;
  }

  // Set up container
  pageContent.innerHTML = `<div id="${containerId}" class="page auth-page-container"></div>`;

  try {
    // Use static imports that Vite can analyze
    let module;
    if (page === 'login') {
      module = await import('./pages/login/index.js');
    } else if (page === 'register') {
      module = await import('./pages/register/index.js');
    } else if (page === 'verify-email') {
      module = await import('./pages/verify-email/index.js');
    } else if (page === 'forgot-password') {
      module = await import('./pages/forgot-password/index.js');
    }
    const mod = module.default || module;

    // Store current auth page for cleanup later
    currentAuthPageModule = mod;
    currentAuthPageName = page;

    // Call the init function (e.g., initLoginPage, initRegisterPage)
    const initFn = mod[`init${page.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('')}Page`];
    if (initFn) {
      initFn();
    } else if (mod.init) {
      mod.init();
    }
  } catch (err) {
    console.error(`[Main] Failed to load ${page} page:`, err);
    pageContent.innerHTML = `<div class="error">Failed to load ${page} page</div>`;
  }
}

/**
 * Restore main layout (show sidebar and header)
 */
function restoreMainLayout() {
  // Clean up current auth page if exists
  if (currentAuthPageModule) {
    console.log('[Main] Cleaning up auth page before restoring layout:', currentAuthPageName);
    const destroyFnName = `destroy${currentAuthPageName.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('')}Page`;
    if (currentAuthPageModule[destroyFnName]) {
      currentAuthPageModule[destroyFnName]();
    } else if (currentAuthPageModule.destroy) {
      currentAuthPageModule.destroy();
    }
    currentAuthPageModule = null;
    currentAuthPageName = null;
  }

  // Remove the auth hiding style if it exists
  const hideStyle = document.getElementById('auth-layout-hide');
  if (hideStyle) {
    hideStyle.remove();
  }

  // Remove auth page background overrides (login page dark background)
  const bgOverride = document.getElementById('login-page-bg-override');
  if (bgOverride) {
    bgOverride.remove();
  }

  // Also remove any other auth page style overrides
  const loginStyles = document.getElementById('login-page-styles');
  if (loginStyles) {
    loginStyles.remove();
  }

  const sidebar = document.querySelector('.sidebar');
  const header = document.querySelector('.main-header');
  const mainContent = document.querySelector('.main-content');
  const copilotPanel = document.getElementById('copilotPanel');

  if (sidebar) sidebar.style.display = '';
  if (header) header.style.display = '';
  if (mainContent) {
    mainContent.style.marginLeft = '';
    mainContent.style.width = '';
  }
  // Restore copilot panel for authenticated users
  if (copilotPanel) copilotPanel.style.display = '';
}

/**
 * Handle OAuth callback from Google/Microsoft
 */
async function handleOAuthCallback(query) {
  const { provider, code, error, error_description } = query;

  // Show loading state
  const pageContent = document.getElementById('pageContent');
  if (pageContent) {
    pageContent.innerHTML = `
      <div class="auth-callback-container" style="display: flex; align-items: center; justify-content: center; min-height: 100vh; background: linear-gradient(135deg, var(--primary-light) 0%, var(--gray-100) 100%);">
        <div style="text-align: center; background: white; padding: 2rem; border-radius: 1rem; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);">
          <div class="loading-spinner" style="width: 40px; height: 40px; border: 3px solid var(--gray-200); border-top-color: var(--primary); border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 1rem;"></div>
          <p style="color: var(--gray-600);">${t('auth.processing') || 'Processing authentication...'}</p>
        </div>
      </div>
      <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
    `;
  }

  // Check for error from OAuth provider
  if (error) {
    console.error('[Main] OAuth error:', error, error_description);
    window.location.hash = '#/login';
    return;
  }

  // Verify we have the required parameters
  if (!provider || !code) {
    console.error('[Main] Missing OAuth callback parameters');
    window.location.hash = '#/login';
    return;
  }

  try {
    // Exchange the authorization code for tokens
    await auth.handleOAuthCallback(provider, code);

    // Redirect to dashboard on success
    window.location.hash = '#/';
  } catch (err) {
    console.error('[Main] OAuth callback error:', err);
    // Redirect to login with error
    window.location.hash = '#/login';
  }
}

/**
 * Navigate to a page
 */
function navigateTo(page, query = {}) {
  // Store query params globally for components to access
  window.currentQuery = query;

  // Handle OAuth callback
  if (page === 'auth/callback') {
    handleOAuthCallback(query);
    return;
  }

  // Auth pages - render in full screen without sidebar
  if (page === 'login' || page === 'register' || page === 'verify-email' || page === 'forgot-password') {
    console.log('[Main] Rendering auth page:', page);
    renderAuthPage(page);
    return;
  }

  // Restore main layout for non-auth pages
  restoreMainLayout();

  // Update navigation active state
  document.querySelectorAll('.nav-item').forEach(item => {
    item.classList.toggle('active', item.dataset.page === page);
  });

  // Update page title
  const pageTitle = document.getElementById('pageTitle');
  if (pageTitle) {
    const titleKey = pageTitleKeys[page] || 'nav.dashboard';
    pageTitle.textContent = t(titleKey);
  }

  // Update breadcrumb
  const breadcrumb = document.querySelector('.breadcrumb');
  if (breadcrumb) {
    const titleKey = pageTitleKeys[page] || 'nav.dashboard';
    breadcrumb.textContent = `${t('common.home')} / ${t(titleKey)}`;
  }

  // Render page content
  const pageContent = document.getElementById('pageContent');

  // Special handling for upload page
  if (page === 'upload') {
    console.log('[Main] Navigating to upload page with query:', query);
    pageContent.innerHTML = '<div id="upload-page" class="page upload-page-container"></div>';

    // Check if documents were passed from modal
    if (window.uploadedDocuments && window.uploadedDocuments.length > 0) {
      console.log('[Main] Found uploaded documents:', window.uploadedDocuments.length);
      console.log('[Main] First document ID:', window.uploadedDocuments[0]?.id);
      // Set state before init so it renders in preview mode
      const pageState = uploadPage.default.getState();
      console.log('[Main] Got page state:', pageState ? 'yes' : 'no');
      if (pageState) {
        pageState.uploadedDocuments = window.uploadedDocuments;
        pageState.selectedDocumentId = window.uploadedDocuments[0].id;
        pageState.view = 'preview';
        pageState.currentPreviewIndex = 0;
        console.log('[Main] State updated:', {
          view: pageState.view,
          selectedDocumentId: pageState.selectedDocumentId
        });
      }
      window.uploadedDocuments = null; // Clear after use
    } else {
      console.log('[Main] No uploaded documents found, will render upload view');
    }

    uploadPage.init();
    return;
  }

  // Helper function to create page loading state
  // Inline styles ensure proper sizing even before CSS loads to prevent SVG flash
  const createPageLoading = (containerId, containerClass) => `
    <div id="${containerId}" class="page ${containerClass}">
      <div class="page-loading" style="display:flex;align-items:center;justify-content:center;min-height:200px;opacity:0;animation:fadeIn 0.2s ease-out 0.1s forwards;">
        <div class="loading-spinner" style="text-align:center;">
          <svg class="spinner-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:32px;height:32px;color:var(--primary,#2563eb);animation:spin 1s linear infinite;">
            <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
            <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/>
          </svg>
          <p style="margin-top:0.75rem;color:var(--gray-500,#6b7280);font-size:0.875rem;">${t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    </div>
    <style>@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes spin{to{transform:rotate(360deg)}}</style>
  `;

  // Special handling for contracts page - use module with API calls
  if (page === 'contracts') {
    pageContent.innerHTML = createPageLoading('contracts-page', 'contracts-page-container');
    contractsPage.default.mounted();
    return;
  }

  // Special handling for invoices page - use module with API calls
  if (page === 'invoices') {
    pageContent.innerHTML = createPageLoading('app-content', 'invoices-page-container');
    invoicesPage.default.mounted();
    return;
  }

  // Special handling for renewals page - use module with API calls
  if (page === 'renewals') {
    pageContent.innerHTML = createPageLoading('renewals-page', 'renewals-page-container');
    renewalsPage.init();
    return;
  }

  // Special handling for vendors page - use module with API calls
  if (page === 'vendors') {
    pageContent.innerHTML = createPageLoading('vendors-page', 'vendors-page-container');
    vendorsPage.default.mounted();
    return;
  }

  // Special handling for discrepancies page
  if (page === 'discrepancies') {
    pageContent.innerHTML = createPageLoading('discrepancies-page', 'discrepancies-page-container');
    discrepanciesPage.default.mounted();
    return;
  }

  // Handle discrepancy detail pages (discrepancies/:id)
  if (page.startsWith('discrepancies/')) {
    const discrepancyId = page.split('/')[1];
    if (discrepancyId) {
      pageContent.innerHTML = createPageLoading('app-content', 'discrepancy-detail-container');
      import('./pages/discrepancies/[id].js').then(module => {
        module.default.mounted({ id: discrepancyId });
      }).catch(err => {
        console.error('[Main] Failed to load discrepancy detail:', err);
        pageContent.innerHTML = '<div class="error">Failed to load discrepancy details</div>';
      });
      return;
    }
  }

  // Special handling for analysis dashboard page
  if (page === 'analysis') {
    pageContent.innerHTML = createPageLoading('app-content', 'analysis-page-container');
    import('./pages/analysis/index.js').then(module => {
      module.default.mounted();
    }).catch(err => {
      console.error('[Main] Failed to load analysis page:', err);
      pageContent.innerHTML = '<div class="error">Failed to load analysis page</div>';
    });
    return;
  }

  // Handle contract analysis pages (contracts/:id/analysis)
  if (page.match(/^contracts\/[^/]+\/analysis$/)) {
    const contractId = page.split('/')[1];
    if (contractId) {
      pageContent.innerHTML = createPageLoading('contract-analysis-content', 'contract-analysis-page-container');
      import('./pages/contracts/analysis.js').then(module => {
        console.log('[Main] Contract analysis module loaded:', module);
        const container = document.getElementById('contract-analysis-content');
        if (container) {
          container.innerHTML = module.render();
        }
        module.init({ id: contractId });
      }).catch(err => {
        console.error('[Main] Failed to load contract analysis:', err);
        console.error('[Main] Error stack:', err.stack);
        pageContent.innerHTML = `<div class="error">Failed to load contract analysis: ${err.message}</div>`;
      });
      return;
    }
  }

  // Handle contract detail pages (contracts/:id)
  if (page.startsWith('contracts/')) {
    const contractId = page.split('/')[1];
    if (contractId) {
      pageContent.innerHTML = createPageLoading('app-content', 'contract-detail-container');
      import('./pages/contracts/[id].js').then(module => {
        module.default.mounted({ id: contractId });
      }).catch(err => {
        console.error('[Main] Failed to load contract detail:', err);
        pageContent.innerHTML = '<div class="error">Failed to load contract details</div>';
      });
      return;
    }
  }

  // Handle vendor detail pages (vendors/:id)
  if (page.startsWith('vendors/')) {
    const vendorId = page.split('/')[1];
    if (vendorId) {
      pageContent.innerHTML = createPageLoading('app-content', 'vendor-detail-container');
      import('./pages/vendors/[id].js').then(module => {
        module.default.mounted({ id: vendorId });
      }).catch(err => {
        console.error('[Main] Failed to load vendor detail:', err);
        pageContent.innerHTML = '<div class="error">Failed to load vendor details</div>';
      });
      return;
    }
  }

  // Handle invoice analysis pages (invoices/:id/analysis)
  if (page.match(/^invoices\/[^/]+\/analysis$/)) {
    const invoiceId = page.split('/')[1];
    if (invoiceId) {
      pageContent.innerHTML = createPageLoading('analysis-content', 'invoice-analysis-page-container') + '<div id="modal-container"></div>';
      import('./pages/invoices/analysis.js').then(module => {
        console.log('[Main] Invoice analysis module loaded:', module);
        const container = document.getElementById('analysis-content');
        if (container) {
          container.innerHTML = module.render();
        }
        module.init({ id: invoiceId });
      }).catch(err => {
        console.error('[Main] Failed to load invoice analysis:', err);
        console.error('[Main] Error stack:', err.stack);
        pageContent.innerHTML = `<div class="error">Failed to load invoice analysis: ${err.message}</div>`;
      });
      return;
    }
  }

  // Handle invoice edit pages (invoices/:id/edit)
  if (page.match(/^invoices\/[^/]+\/edit$/)) {
    const invoiceId = page.split('/')[1];
    if (invoiceId) {
      pageContent.innerHTML = createPageLoading('app-content', 'invoice-edit-container');
      import('./pages/invoices/edit.js').then(module => {
        module.default.mounted({ id: invoiceId });
      }).catch(err => {
        console.error('[Main] Failed to load invoice edit:', err);
        pageContent.innerHTML = `<div class="error">Failed to load invoice edit: ${err.message}</div>`;
      });
      return;
    }
  }

  // Handle invoice detail pages (invoices/:id)
  if (page.startsWith('invoices/')) {
    const invoiceId = page.split('/')[1];
    if (invoiceId) {
      pageContent.innerHTML = createPageLoading('app-content', 'invoice-detail-container');
      import('./pages/invoices/[id].js').then(module => {
        module.default.mounted({ id: invoiceId });
      }).catch(err => {
        console.error('[Main] Failed to load invoice detail:', err);
        pageContent.innerHTML = '<div class="error">Failed to load invoice details</div>';
      });
      return;
    }
  }

  // Special handling for integrations page - use dynamic import
  if (page === 'integrations') {
    pageContent.innerHTML = createPageLoading('integrations-page', 'integrations-page-container');

    import('./pages/integrations/index.js').then(module => {
      module.init();
    }).catch(err => {
      console.error('[Main] Failed to load integrations page:', err);
      pageContent.innerHTML = '<div class="error">Failed to load integrations page</div>';
    });
    return;
  }
  
  // Special handling for optimization page - use dynamic import
  if (page === 'optimization') {
    pageContent.innerHTML = createPageLoading('optimization-page', 'optimization-page-container');
    import('./pages/optimization/index.js').then(module => {
      pageContent.innerHTML = module.renderOptimizationPage();
      module.initOptimizationPage();
    }).catch(err => {
      console.error('[Main] Failed to load optimization page:', err);
      pageContent.innerHTML = '<div class="error">Failed to load optimization page</div>';
    });
    return;
  }

  // Special handling for audit page - use dynamic import
  if (page === 'audit') {
    pageContent.innerHTML = createPageLoading('audit-page', 'audit-page-container');
    import('./pages/settings/audit.js').then(module => {
      pageContent.innerHTML = module.render();
      module.init();
    }).catch(err => {
      console.error('[Main] Failed to load audit page:', err);
      pageContent.innerHTML = '<div class="error">Failed to load audit page</div>';
    });
    return;
  }

  // Special handling for admin pages - super admin only
  if (page === 'admin' || page.startsWith('admin/')) {
    // Ensure nav visibility is correct for admin pages
    updateNavVisibilityFromToken();

    // Show loading state while admin module loads
    pageContent.innerHTML = `
      <div id="app" class="page admin-page-container">
        <div style="display: flex; align-items: center; justify-content: center; min-height: 200px;">
          <div class="loading-spinner" style="width: 40px; height: 40px; border: 3px solid var(--gray-200, #e5e7eb); border-top-color: var(--primary, #2563eb); border-radius: 50%; animation: spin 0.8s linear infinite;"></div>
        </div>
      </div>
    `;

    // Determine which admin page to load - use explicit imports for Vite bundling
    let importPromise;
    if (page === 'admin/organizations' || page.startsWith('admin/organizations/')) {
      importPromise = import('./pages/admin/organizations.js');
    } else if (page === 'admin/users') {
      importPromise = import('./pages/admin/users.js');
    } else if (page === 'admin/analytics') {
      importPromise = import('./pages/admin/analytics.js');
    } else if (page === 'admin/plans') {
      importPromise = import('./pages/admin/plans.js');
    } else if (page === 'admin/billing') {
      importPromise = import('./pages/admin/billing.js');
    } else {
      importPromise = import('./pages/admin/index.js');
    }

    importPromise.then(module => {
      const mod = module.default || module;
      if (mod.init) {
        mod.init();
      }
    }).catch(err => {
      console.error('[Main] Failed to load admin page:', err);
      pageContent.innerHTML = '<div class="error">Failed to load admin page</div>';
    });
    return;
  }

  // Special handling for accept-invitation page (public)
  if (page.startsWith('accept-invitation/')) {
    pageContent.innerHTML = createPageLoading('app', 'invitation-page-container');
    import('./pages/accept-invitation.js').then(module => {
      const mod = module.default || module;
      if (mod.init) {
        mod.init();
      }
    }).catch(err => {
      console.error('[Main] Failed to load accept-invitation page:', err);
      pageContent.innerHTML = '<div class="error">Failed to load invitation page</div>';
    });
    return;
  }

  // Special handling for settings page - use dynamic import with real API
  // Each import must use a literal string for Vite to code-split properly
  if (page === 'settings' || page.startsWith('settings/')) {
    pageContent.innerHTML = createPageLoading('app-content', 'settings-page-container');

    const handleSettingsModule = (module) => {
      const mod = module.default || module;
      const appContent = document.getElementById('app-content');
      if (mod.render && appContent) {
        appContent.innerHTML = mod.render();
      }
      if (mod.init) {
        mod.init();
      }
    };

    const handleSettingsError = (err) => {
      console.error('[Main] Failed to load settings page:', err);
      pageContent.innerHTML = '<div class="error">Failed to load settings page</div>';
    };

    // Use literal strings for each import to enable code-splitting
    if (page === 'settings/profile') {
      import('./pages/settings/profile.js').then(handleSettingsModule).catch(handleSettingsError);
    } else if (page === 'settings/notifications') {
      import('./pages/settings/notifications.js').then(handleSettingsModule).catch(handleSettingsError);
    } else if (page === 'settings/audit') {
      import('./pages/settings/audit.js').then(handleSettingsModule).catch(handleSettingsError);
    } else if (page === 'settings/users') {
      import('./pages/settings/users.js').then(handleSettingsModule).catch(handleSettingsError);
    } else if (page === 'settings/invitations') {
      import('./pages/settings/invitations.js').then(handleSettingsModule).catch(handleSettingsError);
    } else if (page === 'settings/registrations') {
      import('./pages/settings/registrations.js').then(handleSettingsModule).catch(handleSettingsError);
    } else if (page === 'settings/organization') {
      import('./pages/settings/organization.js').then(handleSettingsModule).catch(handleSettingsError);
    } else if (page === 'settings/usage') {
      import('./pages/settings/usage.js').then(handleSettingsModule).catch(handleSettingsError);
    } else if (page === 'settings/billing') {
      import('./pages/settings/billing.js').then(handleSettingsModule).catch(handleSettingsError);
    } else {
      // Default settings page
      import('./pages/settings/index.js').then(handleSettingsModule).catch(handleSettingsError);
    }
    return;
  }

  const renderer = pageRenderers[page];
  
  if (renderer && pageContent) {
    pageContent.innerHTML = renderer();
    setupPageInteractions(page);
  }
  
  // Re-apply translations to navigation
  applyTranslations();
}

/**
 * Setup page-specific interactions
 */
function setupPageInteractions(page) {
  // Contracts page - row click to show detail
  if (page === 'contracts') {
    document.querySelectorAll('[data-contract-id]').forEach(row => {
      row.addEventListener('click', () => {
        showContractModal(row.dataset.contractId);
      });
    });
  }

  // Invoices page - row click to show detail
  if (page === 'invoices') {
    document.querySelectorAll('[data-invoice-id]').forEach(row => {
      row.addEventListener('click', () => {
        showInvoiceModal(row.dataset.invoiceId);
      });
    });

    // Upload button
    const uploadBtn = document.getElementById('uploadInvoiceBtn');
    if (uploadBtn) {
      uploadBtn.addEventListener('click', () => {
        document.getElementById('uploadModal').classList.remove('hidden');
      });
    }
  }

  // Renewals page - card click to show contract
  if (page === 'renewals') {
    document.querySelectorAll('.renewal-card[data-contract-id]').forEach(card => {
      card.addEventListener('click', () => {
        showContractModal(card.dataset.contractId);
      });
    });
  }
}

/**
 * Setup upload modal
 */
function setupUploadModal() {
  const uploadBtn = document.getElementById('uploadBtn');
  const uploadModal = document.getElementById('uploadModal');
  const closeModal = document.getElementById('closeModal');
  const cancelUpload = document.getElementById('cancelUpload');
  const uploadArea = document.getElementById('uploadArea');
  const fileInput = document.getElementById('fileInput');
  const uploadFiles = document.getElementById('uploadFiles');
  const processUpload = document.getElementById('processUpload');

  // Track upload state
  let isUploading = false;

  // Open modal
  uploadBtn?.addEventListener('click', () => {
    uploadModal.classList.remove('hidden');
  });

  // Close modal
  const closeUploadModal = () => {
    if (isUploading) return; // Prevent closing during upload
    uploadModal.classList.add('hidden');
    uploadFiles.innerHTML = '';
    uploadFiles.classList.add('hidden');
    processUpload.disabled = true;
    processUpload.textContent = t('upload.modal.processBtn') || 'Process Documents';
  };

  closeModal?.addEventListener('click', closeUploadModal);
  cancelUpload?.addEventListener('click', closeUploadModal);

  // Close on overlay click
  uploadModal?.addEventListener('click', (e) => {
    if (e.target === uploadModal) {
      closeUploadModal();
    }
  });

  // Upload tabs
  document.querySelectorAll('.upload-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.upload-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
    });
  });

  // Drag and drop
  uploadArea?.addEventListener('dragover', (e) => {
    e.preventDefault();
    uploadArea.classList.add('dragover');
  });

  uploadArea?.addEventListener('dragleave', () => {
    uploadArea.classList.remove('dragover');
  });

  uploadArea?.addEventListener('drop', (e) => {
    e.preventDefault();
    uploadArea.classList.remove('dragover');
    handleFiles(e.dataTransfer.files);
  });

  // Click to browse
  uploadArea?.addEventListener('click', () => {
    fileInput.click();
  });

  fileInput?.addEventListener('change', () => {
    handleFiles(fileInput.files);
  });

  // Handle files - add progress bar HTML
  function handleFiles(files) {
    if (files.length === 0) return;

    uploadFiles.classList.remove('hidden');

    Array.from(files).forEach((file, index) => {
      const fileItem = document.createElement('div');
      fileItem.className = 'upload-file-item';
      fileItem.dataset.fileIndex = index;
      fileItem.innerHTML = `
        <div class="upload-file-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
        </div>
        <div class="upload-file-info">
          <div class="upload-file-name">${file.name}</div>
          <div class="upload-file-meta">
            <span class="upload-file-size">${formatFileSize(file.size)}</span>
            <span class="upload-file-status"></span>
          </div>
          <div class="upload-progress-container hidden">
            <div class="upload-progress-bar">
              <div class="upload-progress-fill"></div>
            </div>
            <span class="upload-progress-text">0%</span>
          </div>
        </div>
        <button class="upload-file-remove" title="Remove">×</button>
      `;

      fileItem.querySelector('.upload-file-remove').addEventListener('click', () => {
        if (isUploading) return;
        fileItem.remove();
        if (uploadFiles.children.length === 0) {
          uploadFiles.classList.add('hidden');
          processUpload.disabled = true;
        }
      });

      uploadFiles.appendChild(fileItem);
    });

    processUpload.disabled = false;
  }

  // Update file item status
  function updateFileStatus(fileItem, status, progress = null) {
    const statusEl = fileItem.querySelector('.upload-file-status');
    const progressContainer = fileItem.querySelector('.upload-progress-container');
    const progressFill = fileItem.querySelector('.upload-progress-fill');
    const progressText = fileItem.querySelector('.upload-progress-text');
    const removeBtn = fileItem.querySelector('.upload-file-remove');

    // Status messages
    const statusMessages = {
      pending: '',
      uploading: t('upload.status.uploading') || 'Uploading...',
      extracting: t('upload.status.extracting') || 'Extracting data...',
      complete: t('upload.status.complete') || 'Complete',
      error: t('upload.status.error') || 'Error'
    };

    // Update status text and class
    fileItem.className = 'upload-file-item upload-status-' + status;
    statusEl.textContent = statusMessages[status] || '';
    statusEl.className = 'upload-file-status status-' + status;

    // Show/hide progress bar
    if (status === 'uploading' || status === 'extracting') {
      progressContainer.classList.remove('hidden');
      removeBtn.classList.add('hidden');
    } else {
      progressContainer.classList.add('hidden');
      removeBtn.classList.toggle('hidden', status === 'complete' || status === 'error');
    }

    // Update progress bar
    if (progress !== null && progressFill && progressText) {
      progressFill.style.width = `${progress}%`;
      progressText.textContent = `${Math.round(progress)}%`;

      // Add animation class for extracting state
      if (status === 'extracting') {
        progressFill.classList.add('extracting-pulse');
      } else {
        progressFill.classList.remove('extracting-pulse');
      }
    }
  }

  // Process upload with XMLHttpRequest for progress tracking
  processUpload?.addEventListener('click', async () => {
    // Collect files from the upload list
    const fileItems = uploadFiles.querySelectorAll('.upload-file-item');
    if (fileItems.length === 0) {
      return;
    }

    // Get files from the file input
    const files = fileInput.files;
    if (!files || files.length === 0) {
      alert('No files selected');
      return;
    }

    // Set uploading state
    isUploading = true;
    processUpload.textContent = t('upload.status.uploading') || 'Uploading...';
    processUpload.disabled = true;

    // Get selected document type from active tab
    const activeTab = document.querySelector('.upload-tab.active');
    const documentType = activeTab?.dataset.type || 'contract';

    // Create FormData
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });
    formData.append('document_type', documentType);

    // Get auth token from auth service storage
    let authToken = null;
    try {
      const authData = localStorage.getItem('auth');
      if (authData) {
        const { accessToken, expiresAt } = JSON.parse(authData);
        if (expiresAt && new Date(expiresAt) > new Date()) {
          authToken = accessToken;
        }
      }
    } catch (e) {
      console.warn('Failed to get auth token:', e);
    }

    if (!authToken) {
      console.error('No valid auth token found');
      return;
    }

    // Update all file items to uploading state
    fileItems.forEach(item => {
      updateFileStatus(item, 'uploading', 0);
    });

    // Create XMLHttpRequest for progress tracking
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const percentComplete = (e.loaded / e.total) * 100;
        // Update all file items with upload progress
        fileItems.forEach(item => {
          updateFileStatus(item, 'uploading', percentComplete);
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText);

          // Switch to extracting state
          processUpload.textContent = t('upload.status.extracting') || 'Extracting...';
          fileItems.forEach(item => {
            updateFileStatus(item, 'extracting', 100);
          });

          // Simulate extraction progress (actual extraction happens on backend)
          let extractProgress = 0;
          const extractInterval = setInterval(() => {
            extractProgress += Math.random() * 15;
            if (extractProgress >= 95) {
              extractProgress = 95;
            }
            fileItems.forEach(item => {
              updateFileStatus(item, 'extracting', extractProgress);
            });
          }, 500);

          // Wait a bit then complete (extraction is already done by this point)
          setTimeout(() => {
            clearInterval(extractInterval);

            // Mark as complete
            fileItems.forEach(item => {
              updateFileStatus(item, 'complete', 100);
            });

            processUpload.textContent = t('upload.status.complete') || 'Complete!';

            if (result.documents && result.documents.length > 0) {
              console.log('Upload successful, documents:', result.documents);

              // Store documents and navigate after short delay
              window.uploadedDocuments = result.documents;

              setTimeout(() => {
                isUploading = false;
                closeUploadModal();
                window.location.hash = 'upload';
              }, 800);
            } else {
              isUploading = false;
              alert('Upload completed but no documents were processed.');
              processUpload.textContent = t('upload.modal.processBtn') || 'Process Documents';
              processUpload.disabled = false;
            }
          }, 2000);

        } catch (error) {
          console.error('Parse error:', error);
          handleUploadError('Failed to parse response');
        }
      } else {
        handleUploadError(`Upload failed: ${xhr.statusText}`);
      }
    });

    xhr.addEventListener('error', () => {
      handleUploadError('Network error occurred');
    });

    xhr.addEventListener('timeout', () => {
      handleUploadError('Upload timed out');
    });

    function handleUploadError(message) {
      isUploading = false;
      console.error('Upload error:', message);
      fileItems.forEach(item => {
        updateFileStatus(item, 'error', 0);
      });
      alert(message);
      processUpload.textContent = t('upload.modal.processBtn') || 'Process Documents';
      processUpload.disabled = false;
    }

    xhr.open('POST', '/api/v1/documents/batch');
    xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    xhr.timeout = 300000; // 5 minute timeout
    xhr.send(formData);
  });
}

/**
 * Setup detail modals
 */
function setupDetailModals() {
  // Close buttons for all modals
  document.querySelectorAll('[data-close]').forEach(btn => {
    btn.addEventListener('click', () => {
      const modalId = btn.dataset.close;
      document.getElementById(modalId)?.classList.add('hidden');
    });
  });

  // Close on overlay click
  document.querySelectorAll('.modal-overlay').forEach(overlay => {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.classList.add('hidden');
      }
    });
  });
}

/**
 * Show contract detail modal
 */
function showContractModal(contractId) {
  const modal = document.getElementById('contractModal');
  const body = document.getElementById('contractModalBody');
  
  if (modal && body) {
    body.innerHTML = renderContractDetail(contractId);
    modal.classList.remove('hidden');
  }
}

/**
 * Show invoice detail modal
 */
function showInvoiceModal(invoiceId) {
  const modal = document.getElementById('invoiceModal');
  const body = document.getElementById('invoiceModalBody');
  
  if (modal && body) {
    body.innerHTML = renderInvoiceDetail(invoiceId);
    modal.classList.remove('hidden');
  }
}

/**
 * Format file size
 */
function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Initialize mobile sidebar toggle
 */
function initMobileSidebar() {
  const sidebar = document.getElementById('sidebar');
  const overlay = document.getElementById('sidebarOverlay');
  const toggleBtn = document.getElementById('mobileMenuToggle');
  const closeBtn = document.getElementById('sidebarClose');

  if (!sidebar || !overlay || !toggleBtn) return;

  function openSidebar() {
    sidebar.classList.add('open');
    overlay.classList.add('visible');
    document.body.style.overflow = 'hidden';
  }

  function closeSidebar() {
    sidebar.classList.remove('open');
    overlay.classList.remove('visible');
    document.body.style.overflow = '';
  }

  // Toggle button opens sidebar
  toggleBtn.addEventListener('click', openSidebar);

  // Close button closes sidebar
  if (closeBtn) {
    closeBtn.addEventListener('click', closeSidebar);
  }

  // Clicking overlay closes sidebar
  overlay.addEventListener('click', closeSidebar);

  // Close sidebar when clicking a nav item (mobile UX)
  sidebar.querySelectorAll('.nav-item').forEach(item => {
    item.addEventListener('click', () => {
      if (window.innerWidth <= 768) {
        closeSidebar();
      }
    });
  });

  // Close sidebar on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sidebar.classList.contains('open')) {
      closeSidebar();
    }
  });

  // Handle window resize - close sidebar if expanding to desktop
  window.addEventListener('resize', () => {
    if (window.innerWidth > 768 && sidebar.classList.contains('open')) {
      closeSidebar();
    }
  });
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  init();
  initMobileSidebar();
});

// Build: 1767108028
